// frontend/src/contexts/StoreDataContext.jsx
//
// Store-scoped reference data shared across the app — locations. Drives
// the location filter on every grid that supports filtering.
//
// Not a React context despite the file name: there is no Provider —
// `useStoreData()` is a plain hook that runs the location query. Consumers
// subscribe to exactly the data they read, so re-renders are scoped.
//
// API: `{locations, error}` — `error` is the query's own error (null while it
// has none); `locations` is `[]` until it has data, so a consumer tells "no
// locations" from "not loaded" by `error`.

import { useQuery } from '@tanstack/react-query';
import { sessionApi } from '../services/SessionApiService';
import { fetchAllPagesVia } from '../utils/fetchAllPages';
import { useAuth } from './AuthContext';
import { locations as locationsKey } from '../queries/keys';

const FIVE_MIN = 5 * 60 * 1000;

// Every row of a paginated list, not the first page of one: a page-size cap
// is the server's limit, not a fact about the store. See
// utils/fetchAllPages.js.
function fetchAllListResults(path, label) {
  return () => fetchAllPagesVia(sessionApi, path, { label });
}

export function useStoreData() {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const enabled = !!storeId;

  const locationsQuery = useQuery({
    queryKey: locationsKey.all(storeId),
    queryFn: fetchAllListResults('/api/locations/', 'locations'),
    staleTime: FIVE_MIN,
    enabled,
  });

  return {
    locations: locationsQuery.data ?? [],
    error: locationsQuery.isError ? locationsQuery.error : null,
  };
}
